import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { api, type Book, type Me } from "../api";
import { ChevronIcon, MoreIcon, PaperStack, PlusIcon } from "../components/icons";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { bookIsShareable, ShareConfirmSheet, ShareDialog, shareUrlFor, useShare } from "../components/share";
import { ViewCountBadge } from "../components/ViewCountBadge";
import { HowStep } from "../components/HowStep";

export default function Home({ me }: { me: Me }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [books, setBooks] = useState<Book[] | null>(null);
  const [archivedCount, setArchivedCount] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [pending, setPending] = useState<{ kind: "archive" | "delete"; book: Book } | null>(null);
  const [busy, setBusy] = useState(false);
  const {
    status: shareStatus,
    dialog: shareDialog,
    pendingShare,
    clearPendingShare,
    runMobileShare,
    share,
    closeDialog: closeShare,
  } = useShare();

  useEffect(() => {
    void api.listBooks("active").then(setBooks).catch((e) => setErr(String(e)));
    void api
      .listBooks("archived")
      .then((rows) => setArchivedCount(rows.length))
      .catch(() => undefined);
  }, []);

  const runArchive = async (book: Book) => {
    setErr(null);
    setBusy(true);
    const prev = books ?? [];
    try {
      await api.archiveBook(book.id);
      setBooks(prev.filter((b) => b.id !== book.id));
      setArchivedCount((n) => n + 1);
      setPending(null);
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const runDelete = async (book: Book) => {
    setErr(null);
    setBusy(true);
    const prev = books ?? [];
    try {
      await api.deleteBook(book.id);
      setBooks(prev.filter((b) => b.id !== book.id));
      setPending(null);
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const titleOf = (b: Book) => (b.title.trim() ? `"${b.title}"` : t("home.thisBook"));

  const startNew = () => {
    navigate("/app/new");
  };

  const initial = (me.name ?? me.email ?? "?").trim().charAt(0).toUpperCase();
  const firstName = (me.authorName ?? me.name ?? me.email).split(/[\s@]/)[0];

  return (
    <div className="tp-app tp-wide">
      <div className="tp-topbar">
        <Link
          to="/app/account"
          style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none", color: "inherit" }}
          aria-label={t("common.account")}
        >
          <div style={{
            width: 38, height: 38, borderRadius: 999,
            background: "var(--tp-accent3)", border: "2px solid var(--tp-ink)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "var(--tp-font-display)", fontWeight: 700, fontSize: 18, color: "var(--tp-ink)",
          }}>{initial}</div>
          <div>
            <div className="tp-eyebrow" style={{ fontSize: 10 }}>{me.email}</div>
            <div style={{ fontFamily: "var(--tp-font-display)", fontSize: 17, fontWeight: 600, lineHeight: 1, color: "var(--tp-ink)" }}>
              {me.authorName ?? me.name ?? t("home.signedIn")}
            </div>
          </div>
        </Link>
        <Link
          to="/app/account"
          className="tp-btn tp-btn-ghost"
          style={{ padding: "8px 14px", fontSize: 16, textDecoration: "none" }}
        >
          {t("common.account")}
        </Link>
      </div>

      <div className="tp-screen" style={{ paddingTop: 24 }}>
        <div className="tp-display" style={{ marginBottom: 8, fontSize: 40 }}>{t("home.greeting", { name: firstName })}</div>
        <div className="tp-body" style={{ color: "var(--tp-ink-soft)", marginBottom: 14, fontSize: 16 }}>
          {!books || books.length === 0
            ? t("home.subEmpty")
            : t("home.subWelcomeBack")}
        </div>

        {me.credits && (
          <div style={{ marginBottom: 24 }}>
            <Link
              to="/app/account"
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                background: "var(--tp-surface-alt)", border: "2px solid var(--tp-ink)",
                borderRadius: 999, padding: "8px 16px",
                textDecoration: "none", color: "var(--tp-ink)",
                fontFamily: "var(--tp-font-body)", fontSize: 15, fontWeight: 600,
                boxShadow: "2px 3px 0 rgba(42,31,18,0.12)",
              }}
            >
              🎟️{" "}
              {me.credits.unlimited
                ? t("home.creditsUnlimited")
                : t("home.creditsBalance", { balance: me.credits.balance ?? 0, perPage: me.creditsPerPage ?? 10 })}
            </Link>
          </div>
        )}

        {err && <div className="tp-error">{err}</div>}

        {books && books.length > 0 && (
          <div className="tp-library" style={{ marginBottom: 24 }}>
            {books.map((b) => (
              <BookCard
                key={b.id}
                book={b}
                onArchive={() => setPending({ kind: "archive", book: b })}
                onDelete={() => setPending({ kind: "delete", book: b })}
                onShare={() =>
                  void share({
                    url: shareUrlFor(b.shortId),
                    title: b.title || undefined,
                  })
                }
              />
            ))}
          </div>
        )}

        {books && books.length === 0 ? (
          <button onClick={startNew} className="tp-card" style={{
            width: "100%", textAlign: "left", cursor: "pointer",
            display: "flex", flexDirection: "column", gap: 14, padding: 22,
            font: "inherit", color: "inherit", border: undefined,
          }}>
            <div className="tp-paper-stack"><PaperStack /></div>
            <div>
              <div className="tp-h2">{t("home.startFirstTitle")}</div>
              <div className="tp-body" style={{ color: "var(--tp-ink-soft)", marginTop: 4, fontSize: 14 }}>
                {t("home.startFirstBody")}
              </div>
            </div>
            <div className="tp-btn tp-btn-primary" style={{ alignSelf: "flex-start" }}>
              <PlusIcon /> {t("common.newBook")}
            </div>
          </button>
        ) : books ? (
          <button className="tp-btn tp-btn-block" onClick={startNew}>
            <PlusIcon /> {t("home.startNew")}
          </button>
        ) : (
          <div className="tp-loading">{t("common.loading")}</div>
        )}

        {archivedCount > 0 && (
          <div style={{ textAlign: "center", marginTop: 14 }}>
            <Link
              to="/app/archive"
              className="tp-eyebrow"
              style={{ color: "var(--tp-ink-soft)", textDecoration: "underline" }}
            >
              {t("home.viewArchived", { count: archivedCount })}
            </Link>
          </div>
        )}

        <div style={{ marginTop: 28 }}>
          <div className="tp-eyebrow" style={{ marginBottom: 14 }}>{t("home.howItWorks")}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <HowStep n="1" title={t("home.step1Title")} body={t("home.step1Body")} />
            <HowStep n="2" title={t("home.step2Title")} body={t("home.step2Body")} />
            <HowStep n="3" title={t("home.step3Title")} body={t("home.step3Body")} />
          </div>
        </div>
      </div>

      <ShareToast status={shareStatus} />
      <ShareDialog
        open={shareDialog !== null}
        url={shareDialog?.url ?? ""}
        title={shareDialog?.title}
        onClose={closeShare}
      />
      <ShareConfirmSheet
        pending={pendingShare}
        onConfirm={({ url, title }) => {
          clearPendingShare();
          void runMobileShare({ url, title });
        }}
        onCancel={clearPendingShare}
      />

      <ConfirmDialog
        open={pending?.kind === "archive"}
        title={t("home.archiveTitle")}
        body={
          <Trans
            i18nKey="home.archiveBody"
            values={{ book: pending ? titleOf(pending.book) : t("home.thisBook") }}
            components={{ strong: <strong style={{ color: "var(--tp-ink)" }} /> }}
          />
        }
        confirmLabel={t("common.archive")}
        cancelLabel={t("home.archiveKeep")}
        busy={busy}
        onConfirm={() => pending && void runArchive(pending.book)}
        onCancel={() => !busy && setPending(null)}
      />
      <ConfirmDialog
        open={pending?.kind === "delete"}
        title={t("home.deleteTitle")}
        body={
          <Trans
            i18nKey="home.deleteBody"
            values={{ book: pending ? titleOf(pending.book) : t("home.thisBook") }}
            components={{ strong: <strong style={{ color: "var(--tp-ink)" }} /> }}
          />
        }
        confirmLabel={t("home.deleteForever")}
        cancelLabel={t("common.cancel")}
        destructive
        busy={busy}
        onConfirm={() => pending && void runDelete(pending.book)}
        onCancel={() => !busy && setPending(null)}
      />
    </div>
  );
}

function BookCard({
  book,
  onArchive,
  onDelete,
  onShare,
}: {
  book: Book;
  onArchive: () => void;
  onDelete: () => void;
  onShare: () => void;
}) {
  const { t } = useTranslation();
  const shareable = bookIsShareable(book);
  const isReady = book.status === "ready";
  const isMaking = book.status === "polishing" || book.status === "queued";
  const label = book.title || t("common.untitledBook");
  return (
    <div style={{ position: "relative" }}>
      <Link className="tp-card" to={`/app/books/${book.id}`} style={{
        display: "flex", gap: 14, alignItems: "center", textDecoration: "none", color: "inherit",
        paddingRight: 56,
      }}>
        <div style={{
          width: 70, height: 88, flexShrink: 0,
          borderRadius: 8, overflow: "hidden",
          border: "2px solid var(--tp-ink)",
          background: "var(--tp-surface-alt)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: "var(--tp-font-display)", fontSize: 32,
        }}>📖</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="tp-h2" style={{ marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</div>
          <div className="tp-body" style={{ fontSize: 13, color: "var(--tp-ink-soft)" }}>
            {book.author.name ? t("home.byAuthor", { name: book.author.name }) : t("home.yourKidsBook")}
          </div>
          <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{
              display: "inline-block", width: 8, height: 8, borderRadius: 999,
              background: isReady ? "var(--tp-accent2)" : isMaking ? "var(--tp-accent3)" : "var(--tp-ink-soft)",
            }} />
            <span className="tp-eyebrow" style={{ fontSize: 10 }}>
              {isReady ? t("home.statusReady") : isMaking ? t("home.statusMaking") : book.status === "draft" ? t("home.statusDraft") : t("home.statusFailed")}
            </span>
            <ViewCountBadge views={book.viewCount} readers={book.uniqueReaderCount} />
          </div>
        </div>
        {isReady && <ChevronIcon />}
      </Link>
      <CardMenu
        disabled={isMaking}
        items={[
          ...(shareable ? [{ label: t("common.share"), onSelect: onShare }] : []),
          { label: t("common.archive"), onSelect: onArchive },
          { label: t("home.menuDelete"), onSelect: onDelete, destructive: true },
        ]}
      />
    </div>
  );
}

/**
 * Compact overflow menu pinned to the top-right of a card. Closes on outside
 * click. `disabled` greys out the button (used while a polish job is in
 * flight — the server rejects the actions anyway, but we hide the affordance).
 */
export function CardMenu({
  items,
  disabled,
}: {
  items: { label: string; onSelect: () => void; destructive?: boolean }[];
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [open]);

  return (
    <div
      className="tp-card-menu"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="tp-card-menu-btn"
        aria-label={t("common.moreActions")}
        disabled={disabled}
        onClick={(e) => {
          e.preventDefault();
          setOpen((v) => !v);
        }}
      >
        <MoreIcon />
      </button>
      {open && (
        <div className="tp-card-menu-pop" role="menu">
          {items.map((it) => (
            <button
              key={it.label}
              type="button"
              role="menuitem"
              className="tp-card-menu-item"
              data-destructive={it.destructive ? "1" : "0"}
              onClick={(e) => {
                e.preventDefault();
                setOpen(false);
                it.onSelect();
              }}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ShareToast({
  status,
  error,
}: {
  status: "idle" | "copied" | "shared" | "error";
  error?: string;
}) {
  const { t } = useTranslation();
  if (status === "idle") return null;
  const msg =
    status === "copied"
      ? t("share.linkCopied")
      : status === "shared"
        ? t("share.shared")
        : (error ?? t("share.copyFailed"));
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        left: "50%",
        bottom: 28,
        transform: "translateX(-50%)",
        background: "var(--tp-ink)",
        color: "var(--tp-surface)",
        padding: "10px 18px",
        borderRadius: 999,
        fontFamily: "var(--tp-font-body)",
        fontSize: 14,
        fontWeight: 600,
        zIndex: 50,
        boxShadow: "0 6px 20px rgba(0,0,0,0.18)",
      }}
    >
      {msg}
    </div>
  );
}

